/**
 * Shared utility functions for case expense components.
 */

import type {
  CaseExpenseType,
  PayeeType,
  ReadCaseExpense,
} from '@/services/api/generated/caseExpensesService';

/**
 * Get display name for a payee from a case expense.
 * Handles all payee types: PROVIDER, COVERAGE, POLICE_DEPARTMENT, CONTACT
 */
export const getPayeeDisplayName = (expense: ReadCaseExpense): string => {
  const { payee } = expense;
  if (!payee) return 'Unknown';

  switch (payee.type) {
    case 'PROVIDER':
      return payee.name ?? 'Unknown Provider';
    case 'COVERAGE':
      return payee.carrier_name ?? 'Unknown Coverage';
    case 'POLICE_DEPARTMENT':
      return payee.name ?? 'Unknown Department';
    case 'CONTACT': {
      const info = payee.contact_info;
      if (info.organization_name) return info.organization_name;
      const nameParts = [info.given_name, info.family_name].filter(Boolean);
      return nameParts.length > 0 ? nameParts.join(' ') : 'Unknown Contact';
    }
    default:
      return 'Unknown';
  }
};

/**
 * Infer the payee type from an expense type.
 * - POLICE -> POLICE_DEPARTMENT
 * - MEDICAL -> PROVIDER
 * - MAIL/OTHER -> CONTACT
 */
export const inferPayeeType = (expenseType: CaseExpenseType): PayeeType => {
  switch (expenseType) {
    case 'POLICE':
      return 'POLICE_DEPARTMENT';
    case 'MEDICAL':
      return 'PROVIDER';
    case 'MAIL':
    case 'OTHER':
    default:
      return 'CONTACT';
  }
};
